import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

function UpsertDialog({ onSaved, initial }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    key: initial?.key || "",
    kind: initial?.kind || "owner_value",
    label: initial?.label || "",
    description: initial?.description || "",
    value: "",
  });
  const isEdit = !!initial;

  const submit = async () => {
    if (!form.key || !form.value) {
      toast.error("Key and value required");
      return;
    }
    setBusy(true);
    try {
      await crmApi.upsertVault(form);
      toast.success(isEdit ? "Vault entry updated" : "Vault entry created");
      setOpen(false);
      onSaved?.();
    } catch (exc) {
      toast.error(exc.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button size="sm" variant="outline" className="border-zinc-700 text-white" data-testid={`vault-edit-${initial.key}`}>Replace value</Button>
        ) : (
          <Button size="sm" className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="vault-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New entry
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-black border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Replace value" : "New vault entry"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Key (snake_case, unique per tenant)</Label>
            <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} disabled={isEdit} data-testid="vault-key" placeholder="duo_pest_wholesale_per_visit_usd" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="vault-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner_value">owner_value</SelectItem>
                  <SelectItem value="secret">secret</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="vault-label" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="vault-description" />
          </div>
          <div>
            <Label>Value</Label>
            <Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="vault-value" placeholder="45.00 (Duo wholesale per visit) or sk_..." />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="vault-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save (encrypted)"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function VaultView() {
  const { ctx } = useOutletContext();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const isOwner = ctx?.role === "owner";

  const reload = async () => {
    setLoading(true);
    try {
      const list = await crmApi.listVault();
      setEntries(list || []);
    } catch (exc) {
      toast.error(exc.message || "Failed to load vault");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await crmApi.listVault();
        if (active) setEntries(list || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load vault");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const remove = async (key) => {
    if (!window.confirm(`Delete vault entry "${key}"?`)) return;
    try {
      await crmApi.deleteVault(key);
      toast.success("Entry deleted");
      reload();
    } catch (exc) {
      toast.error(exc.message || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="owner-vault">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Owner Vault</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Sensitive owner-only values (wholesale rates, partner contract numbers, integration secrets).
            Stored encrypted at rest. <span className="text-amber-300">Never injected into sales-facing agent prompts.</span>
          </p>
        </div>
        {isOwner && <UpsertDialog onSaved={reload} />}
      </div>

      {!isOwner && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardContent className="py-4 text-sm text-amber-200 flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Owner Vault is owner-role only. You can view metadata only.
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : entries.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="py-10 text-center text-sm text-zinc-400">
            <AlertCircle className="h-6 w-6 text-amber-300 mx-auto mb-2" />
            Vault is empty. Add an owner_value (e.g. <code className="text-amber-300">duo_pest_wholesale_per_visit_usd = 45</code>) to start.
          </CardContent>
        </Card>
      ) : (
        <ul className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-900/40">
          {entries.map((e) => (
            <li key={e.id} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`vault-row-${e.key}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm text-white">{e.key}</span>
                  <Badge variant="outline" className={e.kind === "secret" ? "border-rose-500/40 text-rose-300" : "border-emerald-500/40 text-emerald-300"}>
                    {e.kind}
                  </Badge>
                </div>
                {e.label && <div className="text-xs text-zinc-300 mt-1">{e.label}</div>}
                {e.description && <div className="text-xs text-zinc-500 mt-0.5">{e.description}</div>}
                <div className="text-[11px] text-zinc-500 mt-1">
                  updated {new Date(e.updated_at).toLocaleString()}
                </div>
                <div className="mt-1 text-xs">
                  {"value" in e ? (
                    <span className="font-mono text-amber-200">{e.value}</span>
                  ) : (
                    <span className="font-mono text-zinc-500">{e.value_preview || "********"}</span>
                  )}
                </div>
              </div>
              {isOwner && (
                <div className="flex items-center gap-2">
                  <UpsertDialog onSaved={reload} initial={e} />
                  <Button size="sm" variant="ghost" onClick={() => remove(e.key)} className="text-rose-300 hover:bg-rose-500/10" data-testid={`vault-delete-${e.key}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
